'use client';

import { TERMS_HREF_CONFIG } from './constants';
import { LinkEntity, NavigationLinks } from './types';

import {
  Domain,
  DomainLocaleConfig,
  Locale,
  PAGES_TO_PATHS,
  TranslationBlocks,
  Translations,
} from '@/shared/types';

interface GetNavigationLinksParams {
  t: Translations;
  domain: Domain;
  locale: Locale;
}

/**
 * Конфигурация навигационных ссылок для футера.
 *
 * Поддерживает два режима отображения:
 * 1. Обычный (links) — вертикальный список подкатегорий
 * 2. Многоколоночный (multiColumn + columnGroups) — явное разделение на колонки
 *
 * Пример многоколоночной категории:
 * {
 *   name: 'Категория',
 *   multiColumn: true,
 *   columnGroups: [
 *     [подкатегории для левой колонки],
 *     [подкатегории для правой колонки]
 *   ]
 * }
 *
 * См. README.md в этой папке для подробной документации.
 */
export const getNavigationLinks = ({
  t,
  domain,
  locale,
}: GetNavigationLinksParams): NavigationLinks[] => {
  const termsInfoBlock = getTermsInfoBlock({ t, domain, locale });

  return [
    {
      name: t.footerFooterMenuSectionsTechnologyTitle,
      multiColumn: true,
      columnGroups: [
        [
          {
            name: t.footerFooterMenuSectionsTechnologyItemsProducts,
            sublinks: [
              {
                name: t.footerFooterMenuSectionsTechnologyItemsDatabasesName,
                href: t.footerFooterMenuSectionsTechnologyItemsDatabasesLink,
              },
              {
                name: t.productApiSdkName,
                href: t.footerFooterMenuSectionsTechnologyItemsApiSdkLink,
              },
              {
                name: t.productVectorMapsName,
                href: t.footerFooterMenuSectionsTechnologyItemsVectorMapsLink,
              },
              {
                name: t.footerFooterMenuSectionsTechnologyItems2gisProName,
                href: PAGES_TO_PATHS[TranslationBlocks.Pro],
              },
              {
                name: t.footerFooterMenuSectionsTechnologyItemsLensName,
                href: t.linkCityscan,
                domains: ['ru', 'kz'],
                locales: ['ru', 'kz'],
              },
              {
                name: t.geoflowMonitoring,
                href: PAGES_TO_PATHS[TranslationBlocks.Geoflow],
                domains: ['ru', 'kz'],
                locales: ['ru', 'kz'],
              },
              {
                name: t.geoflowLogistics,
                href: PAGES_TO_PATHS[TranslationBlocks.Logistics],
                domains: ['ru'],
                locales: ['ru'],
              },
              {
                name: t.onPremiseName,
                href: t.footerFooterMenuSectionsTechnologyItemsOnPremiseLink,
              },
            ],
          },
          {
            name: t.footerFooterMenuSectionsCasesTitle,
            href: t.footerFooterMenuSectionsCasesLink,
            domains: ['ru'],
          },
        ],
        [
          {
            name: t.footerFooterMenuSectionsSolutionsTitle,
            sublinks: [
              {
                name: t.footerFooterMenuSectionsSolutionsItemsBusinessName,
                href: t.footerFooterMenuSectionsSolutionsItemsBusinessLink,
              },
              {
                name: t.footerFooterMenuSectionsSolutionsItemsGovernmentName,
                href: t.footerFooterMenuSectionsSolutionsItemsGovernmentLink,
              },
            ],
          },
          {
            name: t.footerFooterMenuSectionsDevelopersTitle,
            sublinks: [
              {
                name: t.footerMenuPlatformManagerName,
                href: t.footerMenuPlatformManagerLink,
              },
              {
                name: t.buttonGetDemoAccess,
                href: t.linkPlatformCabinet,
              },
              {
                name: t.footerFooterMenuSectionsDevelopersItemsStylesEditorName,
                href: t.footerFooterMenuSectionsDevelopersItemsStylesEditorLink,
              },
            ],
          },
          {
            name: t.documentation,
            href: t.footerFooterMenuSectionsDevelopersItemsDocsLink,
          },
          {
            name: t.footerFooterMenuSectionsQualityTitle,
            href: t.footerFooterMenuSectionsQualityLink,
          },
        ],
      ],
    },
    {
      name: t.footerFooterMenuSectionsOtherProductsTitle,
      links: [
        {
          name: t.forBusiness,
          sublinks: [
            {
              name: t.footerFooterMenuSectionsOtherProductsCategoriesBusinessAdvertisingName,
              href: t.footerFooterMenuSectionsOtherProductsCategoriesBusinessAdvertisingLink,
            },
            {
              name: t.footerFooterMenuSectionsOtherProductsCategoriesBusinessOtelloName,
              href: t.footerFooterMenuSectionsOtherProductsCategoriesBusinessOtelloLink,
              domains: ['ru'],
            },
          ],
        },
        {
          name: t.footerFooterMenuSectionsOtherProductsCategoriesUsersTitle,
          sublinks: [
            {
              name: t.footerFooterMenuSectionsOtherProductsCategoriesUsersServiceName,
              href: t.footerFooterMenuSectionsOtherProductsCategoriesUsersServiceLink,
            },
            {
              name: t.footerFooterMenuSectionsOtherProductsCategoriesUsersBookingName,
              href: t.footerFooterMenuSectionsOtherProductsCategoriesUsersBookingLink,
              domains: ['ru'],
            },
          ],
        },
      ],
    },
    {
      name: t.navCompanyName,
      links: [
        {
          sublinks: [
            {
              name: t.footerFooterMenuSectionsCompanyItemsTechnoName,
              href: t.footerFooterMenuSectionsCompanyItemsTechnoLink,
            },
            {
              name: t.footerFooterMenuSectionsCompanyItemsJobsName,
              href: t.footerFooterMenuSectionsCompanyItemsJobsLink,
            },
            {
              name: t.footerFooterMenuSectionsCompanyItemsFranchiseName,
              href: t.footerFooterMenuSectionsCompanyItemsFranchiseLink,
            },
            {
              name: t.footerFooterMenuSectionsCompanyItemsPartnerName,
              href: t.footerFooterMenuSectionsCompanyItemsPartnerLink,
              domains: ['ru', 'kz'],
            },
            {
              name: t.navContactsName,
              href: '#contacts',
            },
          ],
        },
        {
          name: t.footerFooterMenuSectionsNewsTitle,
          sublinks: [
            {
              name: t.footerFooterMenuSectionsNewsItemsPressCenterName,
              href: t.footerFooterMenuSectionsNewsItemsPressCenterLink,
            },
            {
              name: t.footerFooterMenuSectionsNewsItemsDeliveryName,
              href: t.footerFooterMenuSectionsNewsItemsDeliveryLink,
            },
          ],
        },
      ],
    },
    {
      name: t.footerFooterMenuSectionsTermsTitle,
      links: [
        {
          sublinks: termsInfoBlock,
        },
        {
          name: t.buttonSubscribe,
          entityType: 'subscribe',
        },
      ],
    },
  ];
};

interface GetTermsInfoBlockParams {
  domain: Domain;
  locale: Locale;
  t: Translations;
}

/**
 * Функция для формирования блока ссылок для правовой информации
 */
const getTermsInfoBlock = (
  params: GetTermsInfoBlockParams,
): LinkEntity[] | undefined => {
  const legalEntity = getEntity({
    config: TERMS_HREF_CONFIG.LEGAL_INFO,
    labelKey: 'footerFooterMenuSectionsTermsItemsLegalInfoName',
    ...params,
  });
  const privacyEntity = getEntity({
    config: TERMS_HREF_CONFIG.PRIVACY_POLICY,
    labelKey: 'footerFooterMenuSectionsTermsItemsPrivacyPolicyName',
    ...params,
  });
  const licenseAgreementEntity = getEntity({
    config: TERMS_HREF_CONFIG.LICENSE_AGREEMENT,
    labelKey: 'footerFooterMenuSectionsTermsItemsLicenseAgreementName',
    ...params,
  });

  return [legalEntity, privacyEntity, licenseAgreementEntity].filter(
    (l) => !!l,
  );
};

/**
 * Функция для выборки сущности из конфигурации по домену и локали
 */
const getEntity = ({
  config,
  t,
  labelKey,
  domain,
  locale,
}: GetTermsInfoBlockParams & {
  config: DomainLocaleConfig;
  labelKey: string;
}) => {
  const href = config[domain]?.[locale];
  const name = t[labelKey];

  return href && name
    ? {
        name,
        href,
      }
    : undefined;
};
